import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { Mail, Key, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

const RegisterForm = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedMethod, setSelectedMethod] = useState<"password" | "magic_link" | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, signInWithMagicLink, loading, error: authError } = useAuth();
  const navigate = useNavigate();

  const handleMethodSelect = (method: "password" | "magic_link") => {
    if (!email) {
      setFormError(t('auth.enter_email_error'));
      return;
    }
    setFormError(null);

    if (method === "magic_link") {
      handleMagicLinkRegister();
    } else {
      setSelectedMethod("password");
    }
  };

  const handleMagicLinkRegister = async () => {
    try {
      const { error } = await signInWithMagicLink(email);
      if (error) throw error;
      setMagicLinkSent(true);
      setSelectedMethod("magic_link");
    } catch (err) {
      console.error("Magic link registration error:", err);
    }
  };

  const handlePasswordRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password || !confirmPassword) {
      setFormError(t('auth.fill_all_fields_error'));
      return;
    }

    if (password !== confirmPassword) {
      setFormError(t('auth.passwords_mismatch'));
      return;
    }

    if (password.length < 6) {
      setFormError(t('auth.password_too_short'));
      return;
    }

    try {
      const result = await register(email, password);
      if (!result.error) {
        navigate(`/registration-confirmation?email=${encodeURIComponent(email)}`);
      }
    } catch (err) {
      console.error("Registration failed:", err);
    }
  };

  const resetState = () => {
    setSelectedMethod(null);
    setMagicLinkSent(false);
    setFormError(null);
    setPassword("");
    setConfirmPassword("");
  };

  // Рендер: Magic Link отправлен
  if (magicLinkSent) {
    return (
      <div className="space-y-6 w-full max-w-md text-center animate-in fade-in slide-in-from-bottom-4">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8" />
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold">{t('auth.check_email_title')}</h2>
          <p className="text-muted-foreground">
            <Trans
              i18nKey="auth.sent_magic_link_register_to"
              values={{ email }}
              components={{ 1: <span className="font-medium text-foreground" /> }}
            />
          </p>
          <p className="text-sm text-muted-foreground">
            {t('auth.click_link_register_instruction')}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={resetState}
          className="w-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.back_button')}
        </Button>
      </div>
    );
  }

  // Рендер: Регистрация с паролем
  if (selectedMethod === "password") {
    return (
      <div className="space-y-6 w-full max-w-md animate-in fade-in slide-in-from-right-4">
        <Button
          variant="ghost"
          onClick={() => setSelectedMethod(null)}
          className="p-0 h-auto hover:bg-transparent text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.back_to_method_selection')}
        </Button>

        <div className="space-y-1">
          <h2 className="text-xl font-semibold">{t('auth.create_password_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('auth.for_account_param', { email })}</p>
        </div>

        <form onSubmit={handlePasswordRegister} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.create_password_label')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            <p className="text-xs text-muted-foreground">{t('auth.min_6_chars')}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('auth.repeat_password')}</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {(formError || authError) && (
            <div className="text-sm text-red-500 font-medium bg-red-50 p-3 rounded-md">
              {formError || authError}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t('auth.creating_account') : t('auth.create_account')}
          </Button>
        </form>
      </div>
    );
  }

  // Рендер: Начальный экран (Email + Выбор)
  return (
    <div className="space-y-6 w-full max-w-md">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('auth.email')}</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFormError(null);
            }}
            required
          />
        </div>

        {(formError || authError) && (
          <div className="text-sm text-red-500 font-medium bg-red-50 p-3 rounded-md">
            {formError || authError}
          </div>
        )}

        <div className="space-y-3 pt-2">
          <button
            type="button"
            onClick={() => handleMethodSelect("magic_link")}
            disabled={!email || loading}
            className="w-full flex items-center p-4 border rounded-lg hover:bg-accent hover:border-accent-foreground/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group text-left"
          >
            <div className="bg-primary/10 p-3 rounded-full mr-4 group-hover:bg-primary/20 transition-colors">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="font-semibold">{t('auth.magic_link_register_title')}</div>
              <div className="text-sm text-muted-foreground">{t('auth.magic_link_register_desc')}</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleMethodSelect("password")}
            disabled={!email || loading}
            className="w-full flex items-center p-4 border rounded-lg hover:bg-accent hover:border-accent-foreground/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group text-left"
          >
            <div className="bg-primary/10 p-3 rounded-full mr-4 group-hover:bg-primary/20 transition-colors">
              <Key className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="font-semibold">{t('auth.password_register_title')}</div>
              <div className="text-sm text-muted-foreground">{t('auth.password_register_desc')}</div>
            </div>
          </button>
        </div>
      </div>

      <div className="text-center text-sm">
        <span className="text-muted-foreground">{t('auth.already_have_account')}</span>{" "}
        <Link to="/login" className="text-brand-600 font-medium hover:underline">
          {t('auth.login_link')}
        </Link>
      </div>
    </div>
  );
};

export default RegisterForm;